import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import FlowGuard from '../../../shared/components/FlowGuard';

const CompanySignUpSuccessContent: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Get company info from navigation state
    const companyEmail = location.state?.email || '';

    const handleContinue = () => {
        // Navigate to recruiter account setup page with pre-filled data
        navigate('/auth/company/admin-recruiter-setup', {
            state: {
                email: companyEmail,
                firstName: location.state?.firstName || '',
                lastName: location.state?.lastName || '',
                companyId: location.state?.companyId || '',
                companyName: location.state?.companyName || '',
                fromCompanySignup: true
            }
        });
    };

    return (
        <div className="min-h-screen bg-[#F8F9FB] flex">
            {/* Left Side - Success Message */}
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-full max-w-[745px] text-center">
                    {/* Logo */}
                    <div className="mb-8">
                        <img
                            src="/branding/logo_blue.svg"
                            alt="Hush"
                            className="h-12 w-auto mx-auto"
                        />
                    </div>

                    {/* Title */}
                    <h1
                        style={{
                            fontFamily: 'Manrope',
                            fontWeight: 700,
                            fontSize: '48px',
                            lineHeight: '100%',
                            color: '#015FA6',
                            marginBottom: '24px'
                        }}
                    >
                        Your company has been created!
                        <svg
                            width="48"
                            height="48"
                            viewBox="0 0 48 48"
                            fill="none"
                            style={{
                                display: 'inline-block',
                                marginLeft: '8px',
                                verticalAlign: 'middle'
                            }}
                        >
                            <circle cx="24" cy="24" r="24" fill="#10B981"/>
                            <path
                                d="M16 24L22 30L34 18"
                                stroke="#FFFFFF"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </h1>

                    {/* Subtitle */}
                    <p
                        style={{
                            fontFamily: 'Manrope',
                            fontWeight: 300,
                            fontSize: '18px',
                            lineHeight: '150%',
                            color: '#015FA6',
                            marginBottom: '72px'
                        }}
                    >
                        Next, let's create your personal recruiter account.
                    </p>

                    {/* Continue Button */}
                    <button
                        onClick={handleContinue}
                        style={{
                            width: '529px',
                            height: '55px',
                            fontFamily: 'Manrope',
                            fontWeight: 600,
                            fontSize: '20px',
                            lineHeight: '28px',
                            color: '#F7FAFC',
                            background: '#015FA6',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#014580';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#015FA6';
                        }}
                    >
                        Continue to My Account
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path
                                d="M5 12h14m-7-7l7 7-7 7"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Right Side - Hero */}
            <div
                className="flex-1"
                style={{
                    position: 'relative',
                    background: '#015FA6',
                    overflow: 'hidden'
                }}
            >
                {/* Gradient Ellipse */}
                <div
                    style={{
                        position: 'absolute',
                        width: '803.19px',
                        height: '792.79px',
                        left: '211px',
                        top: '-362px',
                        background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.8) 0%, rgba(116, 153, 191, 0.465385) 41.83%, rgba(237, 242, 247, 0) 100%)',
                        transform: 'rotate(26.84deg)',
                        borderRadius: '50%'
                    }}
                />

                {/* Hero Text */}
                <div style={{
                    position: 'absolute',
                    width: '821px',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center'
                }}>
                    <h2
                        style={{
                            fontFamily: 'Manrope',
                            fontWeight: 600,
                            fontSize: '40px',
                            lineHeight: '100%',
                            color: '#F8F9FB',
                            marginBottom: '30px'
                        }}
                    >
                        Smarter hiring, human outcomes.
                    </h2>
                    <p
                        style={{
                            fontFamily: 'Manrope',
                            fontWeight: 400,
                            fontSize: '20px',
                            lineHeight: '138.5%',
                            color: '#F8F9FB',
                            maxWidth: '653px',
                            margin: '0 auto'
                        }}
                    >
                        Our AI understands skills, context, and culture fit — so you can focus on people, not paperwork. Hush helps companies find talent with both brains and careers, and candidates find workplaces where they belong.
                    </p>
                </div>

                {/* Pagination Dots */}
                <div style={{
                    position: 'absolute',
                    bottom: '204px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'center'
                }}>
                    <div style={{
                        width: '7px',
                        height: '7px',
                        background: 'rgba(237, 242, 247, 0.3)',
                        border: '1px solid #FFFFFF',
                        borderRadius: '50%'
                    }}></div>
                    <div style={{
                        width: '22.29px',
                        height: '22.29px',
                        border: '2px solid #FFFFFF',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <div style={{
                            width: '8px',
                            height: '8px',
                            background: '#D9D9D9',
                            border: '1px solid #FFFFFF',
                            borderRadius: '50%'
                        }}></div>
                    </div>
                    <div style={{
                        width: '7px',
                        height: '7px',
                        background: '#CBD5E0',
                        border: '1px solid #FFFFFF',
                        borderRadius: '50%'
                    }}></div>
                    <div style={{
                        width: '3.72px',
                        height: '3.72px',
                        background: 'rgba(237, 242, 247, 0.3)',
                        border: '1px solid #FFFFFF',
                        borderRadius: '50%'
                    }}></div>
                    <div style={{
                        width: '3.72px',
                        height: '3.72px',
                        background: 'rgba(237, 242, 247, 0.3)',
                        border: '1px solid #FFFFFF',
                        borderRadius: '50%'
                    }}></div>
                </div>
            </div>
        </div>
    );
};

/**
 * CompanySignUpSuccess Page
 * Protected by FlowGuard - only accessible after company signup flow
 * Direct URL access redirects to company signup page
 */
const CompanySignUpSuccess: React.FC = () => {
    return (
        <FlowGuard
            requiredStateKey="fromCompanySignup"
            fallbackPath="/auth/company/signup"
        >
            <CompanySignUpSuccessContent />
        </FlowGuard>
    );
};

export default CompanySignUpSuccess;
